"use client";

import { HocuspocusProvider } from "@hocuspocus/provider";
import * as React from "react";
import { IndexeddbPersistence } from "y-indexeddb";
import { Doc as YDoc } from "yjs";
import { editorEnv } from "../env";

type CollabContextValue = {
  provider: HocuspocusProvider | null;
  ydoc: YDoc;
  isSynced: boolean;
  error: { message: string } | null;
  localProvider: IndexeddbPersistence | null;
  isSyncedLocal: boolean;
};

type CollabSyncContextValue = {
  isConnected: boolean;
  unsyncedChanges: number;
  lastSavedAt: Date | null;
};

const CollabContext = React.createContext<CollabContextValue>({
  provider: null,
  ydoc: new YDoc(),
  isSynced: false,
  error: null,
  localProvider: null,
  isSyncedLocal: false,
});

const CollabSyncContext = React.createContext<CollabSyncContextValue>({
  isConnected: false,
  unsyncedChanges: 0,
  lastSavedAt: null,
});

export const CollabConsumer = CollabContext.Consumer;
export const CollabSyncConsumer = CollabSyncContext.Consumer;

export const useCollab = (): CollabContextValue => {
  const context = React.useContext(CollabContext);
  if (!context) {
    throw new Error("useCollab must be used within an CollabProvider");
  }
  return context;
};

export const useCollabSync = (): CollabSyncContextValue => {
  const context = React.useContext(CollabSyncContext);
  if (!context) {
    throw new Error("useCollabSync must be used within a CollabProvider");
  }
  return context;
};

export const useCollaboration = (props: {
  room: string;
  isRemoteSyncEnabled: boolean;
}) => {
  const [provider, setProvider] = React.useState<HocuspocusProvider | null>(
    null,
  );
  const [isSynced, setIsSynced] = React.useState<boolean>(false);
  const [error, setError] = React.useState<{ message: string } | null>(null);
  const [localProvider, setLocalProvider] =
    React.useState<IndexeddbPersistence | null>(null);
  const [isSyncedLocal, setIsSyncedLocal] = React.useState<boolean>(false);

  const ydoc = React.useMemo(() => new YDoc(), []);

  React.useEffect(() => {
    const documentName = props.room;
    // TODO: handle localProvider connection status and errors
    // TODDO: move this to a different useEffect so that local instance gets created when the room is not yet set.
    const newLocalProvider = new IndexeddbPersistence(documentName, ydoc);

    const handleSyncedLocal = () => {
      setIsSyncedLocal(true);
    };

    newLocalProvider.on("synced", handleSyncedLocal);

    setLocalProvider(newLocalProvider);

    if (props.isRemoteSyncEnabled === false) return;

    const newProvider = new HocuspocusProvider({
      name: documentName,
      url: editorEnv.NEXT_PUBLIC_COLLAB_SERVER_URL,
      document: ydoc,
      onSynced: () => {
        setIsSynced(true);
      },
      onAuthenticated: () => {
        setError(null);
      },
      onAuthenticationFailed: (data) => {
        console.error("Authentication failed:", data);
        setIsSynced(false);
        setError({ message: "Authentication failed: " + data.reason });
      },
    });

    setProvider(newProvider);

    return () => {
      newProvider.destroy();
      newLocalProvider.destroy();
      newLocalProvider.off("synced", handleSyncedLocal);
    };
  }, [ydoc, props.isRemoteSyncEnabled]);

  return { provider, ydoc, isSynced, error, localProvider, isSyncedLocal };
};

export function CollabProvider(
  props: Readonly<{
    children: React.ReactNode;
    room: string;
    isRemoteSyncEnabled: boolean;
  }>,
) {
  const { provider, ydoc, isSynced, error, localProvider, isSyncedLocal } =
    useCollaboration({
      room: props.room,
      isRemoteSyncEnabled: props.isRemoteSyncEnabled,
    });

  const value = React.useMemo<
    Pick<CollabContextValue, "provider" | "localProvider" | "ydoc">
  >(
    () => ({
      provider,
      localProvider,
      ydoc,
    }),
    [provider, localProvider, ydoc],
  );

  return (
    <CollabContext.Provider
      value={{ ...value, isSynced, error, isSyncedLocal }}
    >
      {props.children}
    </CollabContext.Provider>
  );
}

export function CollabSyncProvider({
  children,
  provider,
}: Readonly<{
  children: React.ReactNode;
  provider: HocuspocusProvider | null;
}>) {
  const [unsyncedChanges, setUnsyncedChanges] = React.useState<number>(0);
  const [isConnected, setIsConnected] = React.useState<boolean>(true);
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);

  React.useEffect(() => {
    if (!provider) return;

    const handleUnsyncedChanges = (unsyncedChanges: { number: number }) => {
      setUnsyncedChanges((prev) => {
        if (prev > 0 && unsyncedChanges.number === 0) {
          // just saved!
          setLastSavedAt(new Date());
        }

        return unsyncedChanges.number;
      });
    };
    const handleConnect = () => {
      setIsConnected(true);
    };
    const handleDisconnect = () => {
      setIsConnected(false);
    };

    provider.on("unsyncedChanges", handleUnsyncedChanges);
    provider.on("connect", handleConnect);
    provider.on("disconnect", handleDisconnect);

    return () => {
      provider.off("unsyncedChanges", handleUnsyncedChanges);
      provider.off("connect", handleConnect);
      provider.off("disconnect", handleDisconnect);
    };
  }, [provider, setUnsyncedChanges, setIsConnected, setLastSavedAt]);

  return (
    <CollabSyncContext.Provider
      value={{ isConnected, unsyncedChanges, lastSavedAt }}
    >
      {children}
    </CollabSyncContext.Provider>
  );
}
